import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Chip,
  TextField,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import api from "../../utils/api";

const getStatusStyles = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "active":
      return {
        color: "#2e7d32",
        backgroundColor: "rgba(46,125,50,0.10)",
        border: "1px solid rgba(46,125,50,0.25)",
      };
    case "expired":
      return {
        color: "#b26a00",
        backgroundColor: "rgba(237,108,2,0.10)",
        border: "1px solid rgba(237,108,2,0.25)",
      };
    case "revoked":
    case "suspended":
      return {
        color: "#d32f2f",
        backgroundColor: "rgba(211,47,47,0.10)",
        border: "1px solid rgba(211,47,47,0.25)",
      };
    default:
      return {
        color: "#5f6b7a",
        backgroundColor: "rgba(95,107,122,0.08)",
        border: "1px solid rgba(95,107,122,0.18)",
      };
  }
};

const formatDateTime = (value) => {
  if (!value) return "N/A";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return "N/A";
  }
};

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    const pad = (n) => String(n).padStart(2, "0");
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const mi = pad(date.getMinutes());

    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  } catch {
    return "";
  }
};

const emptyEditForm = {
  id: null,
  property_id: "",
  member_id: "",
  license_plate: "",
  state: "",
  make: "",
  model: "",
  color: "",
  year: "",
  permit_number: "",
  sticker_number: "",
  permit_type: "resident",
  status: "active",
  start_date: "",
  end_date: "",
  is_primary: false,
  is_blacklisted: false,
  notes: "",
  rfid_tag: "",
  qr_code: "",
  updated_by: localStorage.getItem("userId") || null,
};

const ResidentVehiclesList = ({
  propertyId,
  memberId,
  canEdit = false,
  canDelete = false,
  refreshKey,
  onChange,
}) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [permitTypeFilter, setPermitTypeFilter] = useState("all");

  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchVehicles = useCallback(async () => {
    try {
      setLoading(true);

      const res = await api.get(`/resident-vehicles`, {
        params: {
          property_id: propertyId,
          member_id: memberId || undefined,
        },
      });

      setVehicles(res.data || []);
    } catch (err) {
      console.error("Failed to fetch resident vehicles:", err);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, [propertyId, memberId]);

  useEffect(() => {
    if (propertyId) {
      fetchVehicles();
    }
  }, [fetchVehicles, propertyId, refreshKey]);

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this vehicle?"
    );

    if (!confirmed) return;

    try {
      await api.delete(`/resident-vehicles/${id}`);
      await fetchVehicles();
      if (onChange) await onChange();

      setSnackbar({
        open: true,
        message: "Vehicle deleted successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to delete vehicle:", err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to delete vehicle.",
        severity: "error",
      });
    }
  };

  const handleOpenEdit = (vehicle) => {
    setEditForm({
      id: vehicle.id,
      property_id: vehicle.property_id ?? propertyId ?? "",
      member_id: vehicle.member_id ?? memberId ?? "",
      license_plate: vehicle.license_plate || "",
      state: vehicle.state || "",
      make: vehicle.make || "",
      model: vehicle.model || "",
      color: vehicle.color || "",
      year: vehicle.year || "",
      permit_number: vehicle.permit_number || "",
      sticker_number: vehicle.sticker_number || "",
      permit_type: vehicle.permit_type || "resident",
      status: vehicle.status || "active",
      start_date: toDateTimeLocalValue(vehicle.start_date),
      end_date: toDateTimeLocalValue(vehicle.end_date),
      is_primary: Boolean(vehicle.is_primary),
      is_blacklisted: Boolean(vehicle.is_blacklisted),
      notes: vehicle.notes || "",
      rfid_tag: vehicle.rfid_tag || "",
      qr_code: vehicle.qr_code || "",
      updated_by: localStorage.getItem("userId") || null,
    });
    setEditOpen(true);
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setEditForm(emptyEditForm);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "is_primary" || name === "is_blacklisted") {
      setEditForm((prev) => ({
        ...prev,
        [name]: value === "true",
      }));
      return;
    }

    setEditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveEdit = async () => {
    if (!editForm.id) return;

    if (!editForm.property_id || !editForm.license_plate.trim()) {
      setSnackbar({
        open: true,
        message: "Property and license plate are required.",
        severity: "warning",
      });
      return;
    }

    try {
      setEditSaving(true);

      await api.put(`/resident-vehicles/${editForm.id}`, {
        property_id: editForm.property_id,
        member_id: editForm.member_id || null,
        license_plate: editForm.license_plate,
        state: editForm.state || null,
        make: editForm.make || null,
        model: editForm.model || null,
        color: editForm.color || null,
        year: editForm.year ? Number(editForm.year) : null,
        permit_number: editForm.permit_number || null,
        sticker_number: editForm.sticker_number || null,
        permit_type: editForm.permit_type || "resident",
        status: editForm.status || "active",
        start_date: editForm.start_date || null,
        end_date: editForm.end_date || null,
        is_primary: editForm.is_primary,
        is_blacklisted: editForm.is_blacklisted,
        notes: editForm.notes || null,
        rfid_tag: editForm.rfid_tag || null,
        qr_code: editForm.qr_code || null,
        updated_by: editForm.updated_by || null,
      });

      handleCloseEdit();
      await fetchVehicles();
      if (onChange) await onChange();

      setSnackbar({
        open: true,
        message: "Vehicle updated successfully.",
        severity: "success",
      });
    } catch (err) {
      console.error("Failed to update vehicle:", err);
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to update vehicle.",
        severity: "error",
      });
    } finally {
      setEditSaving(false);
    }
  };

  const filteredVehicles = useMemo(() => {
    return vehicles.filter((vehicle) => {
      const searchText = search.trim().toLowerCase();

      const matchesSearch =
        !searchText ||
        [
          vehicle.license_plate,
          vehicle.make,
          vehicle.model,
          vehicle.color,
          vehicle.state,
          vehicle.permit_number,
          vehicle.sticker_number,
          vehicle.rfid_tag,
          vehicle.notes,
        ]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(searchText));

      const matchesStatus =
        statusFilter === "all" ||
        String(vehicle.status || "").toLowerCase() === statusFilter;

      const matchesPermitType =
        permitTypeFilter === "all" ||
        String(vehicle.permit_type || "").toLowerCase() === permitTypeFilter;

      return matchesSearch && matchesStatus && matchesPermitType;
    });
  }, [vehicles, search, statusFilter, permitTypeFilter]);

  const permitTypes = useMemo(() => {
    const unique = new Set(
      vehicles
        .map((v) => String(v.permit_type || "").toLowerCase())
        .filter(Boolean)
    );
    return Array.from(unique);
  }, [vehicles]);

  const actionButtonSx = {
    textTransform: "none",
    borderRadius: "10px",
    minWidth: 58,
    height: 36,
    px: 1.75,
    fontWeight: 400,
    boxShadow: "none",
    whiteSpace: "nowrap",
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const readonlyFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f7f9fc",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.5,
    minWidth: 128,
    height: 40,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    px: 2.25,
    minWidth: 128,
    height: 40,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!vehicles.length) {
    return (
      <Box
        sx={{
          borderRadius: "16px",
          border: "1px dashed rgba(47,99,173,0.18)",
          backgroundColor: "rgba(47,99,173,0.02)",
          py: 5,
          px: 3,
          textAlign: "center",
        }}
      >
        <DirectionsCarIcon sx={{ fontSize: 34, color: "#8a94a6", mb: 1 }} />
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
        >
          No vehicles yet
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          No resident vehicles were found for this member/property.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "stretch", md: "center" },
            flexDirection: { xs: "column", md: "row" },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="body2"
              sx={{ color: "#6b778c", fontWeight: 500 }}
            >
              Registered vehicles
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "#223142", fontWeight: 700, mt: 0.25 }}
            >
              {filteredVehicles.length} of {vehicles.length} vehicle
              {vehicles.length === 1 ? "" : "s"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              flexWrap: "wrap",
              justifyContent: { xs: "stretch", md: "flex-end" },
            }}
          >
            <TextField
              size="small"
              placeholder="Search plate, make, model..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 250 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#8a94a6" }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              size="small"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{
                minWidth: 130,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Permit Type"
              value={permitTypeFilter}
              onChange={(e) => setPermitTypeFilter(e.target.value)}
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  backgroundColor: "#fff",
                },
              }}
            >
              <MenuItem value="all">All</MenuItem>
              {permitTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: "18px",
            overflow: "hidden",
            border: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "#fff",
            boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
          }}
        >
          <Box
            sx={{
              display: { xs: "none", lg: "grid" },
              gridTemplateColumns: "1.2fr 1.3fr 1.2fr 1.4fr 1.2fr 1fr",
              gap: 2,
              px: 3,
              py: 1.75,
              backgroundColor: "rgba(47,99,173,0.04)",
              borderBottom: "1px solid rgba(47,99,173,0.08)",
            }}
          >
            {[
              "License Plate",
              "Vehicle Info",
              "Permit & Sticker",
              "Status & Dates",
              "Details",
              "Actions",
            ].map((label) => (
              <Typography
                key={label}
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "#5f6b7a",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {label}
              </Typography>
            ))}
          </Box>

          {filteredVehicles.length === 0 ? (
            <Box sx={{ py: 5, px: 3, textAlign: "center" }}>
              <Typography
                variant="body1"
                sx={{ color: "#223142", fontWeight: 600 }}
              >
                No matching vehicles
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
                Try adjusting your search or filters.
              </Typography>
            </Box>
          ) : (
            filteredVehicles.map((vehicle, index) => {
              const statusStyles = getStatusStyles(vehicle.status);

              return (
                <Box
                  key={vehicle.id}
                  sx={{
                    px: 3,
                    py: 2.25,
                    borderBottom:
                      index !== filteredVehicles.length - 1
                        ? "1px solid rgba(47,99,173,0.08)"
                        : "none",
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      lg: "1.2fr 1.3fr 1.2fr 1.4fr 1.2fr 1fr",
                    },
                    gap: 2,
                    alignItems: "start",
                  }}
                >
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#223142", mb: 0.75 }}
                    >
                      {vehicle.license_plate || "No Plate"}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                      {vehicle.state && (
                        <Chip
                          label={vehicle.state}
                          size="small"
                          sx={{
                            borderRadius: "999px",
                            fontWeight: 600,
                            backgroundColor: "rgba(47,99,173,0.08)",
                            color: "#2f63ad",
                          }}
                        />
                      )}

                      {vehicle.is_primary ? (
                        <Chip
                          label="Primary"
                          size="small"
                          sx={{
                            borderRadius: "999px",
                            fontWeight: 600,
                            backgroundColor: "rgba(46,125,50,0.10)",
                            color: "#2e7d32",
                          }}
                        />
                      ) : null}
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="body1"
                      sx={{ color: "#223142", fontWeight: 600 }}
                    >
                      {[vehicle.make, vehicle.model].filter(Boolean).join(" ") ||
                        "N/A"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#6b778c", mt: 0.5 }}
                    >
                      {[vehicle.color || "N/A", vehicle.year || "N/A"].join(" • ")}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "grid", rowGap: 0.5 }}>
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>Permit:</strong> {vehicle.permit_number || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>Sticker:</strong> {vehicle.sticker_number || "N/A"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>Type:</strong> {vehicle.permit_type || "N/A"}
                    </Typography>
                  </Box>

                  <Box>
                    <Chip
                      label={vehicle.status || "Unknown"}
                      size="small"
                      sx={{
                        mb: 1,
                        textTransform: "capitalize",
                        fontWeight: 700,
                        borderRadius: "999px",
                        ...statusStyles,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: "#223142", mb: 0.4 }}
                    >
                      <strong>Start:</strong> {formatDateTime(vehicle.start_date)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>End:</strong> {formatDateTime(vehicle.end_date)}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "grid", rowGap: 0.5 }}>
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>Blacklisted:</strong>{" "}
                      {vehicle.is_blacklisted ? "Yes" : "No"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#223142" }}>
                      <strong>RFID Tag:</strong> {vehicle.rfid_tag || "N/A"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#223142",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      <strong>Notes:</strong> {vehicle.notes || "N/A"}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      justifyContent: { xs: "flex-start", lg: "flex-end" },
                      flexWrap: "wrap",
                    }}
                  >
                    {canEdit && (
                      <Button
                        variant="outlined"
                        startIcon={<EditOutlinedIcon />}
                        sx={actionButtonSx}
                        onClick={() => handleOpenEdit(vehicle)}
                      >
                        Edit
                      </Button>
                    )}

                    {canDelete && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        sx={actionButtonSx}
                        onClick={() => handleDelete(vehicle.id)}
                      >
                        Delete
                      </Button>
                    )}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 3 },
            pt: 2.25,
            pb: 1.5,
            borderBottom: "1px solid rgba(47,99,173,0.08)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <DirectionsCarIcon sx={{ color: "#2f63ad" }} />
            <DialogTitle
              sx={{
                p: 0,
                fontWeight: 700,
                color: "#223142",
              }}
            >
              Edit Resident Vehicle
            </DialogTitle>
          </Box>

          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            Update registration details, permit info, dates, and access fields.
          </Typography>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Box
              sx={{
                p: { xs: 1.5, sm: 2, md: 2.5 },
                borderRadius: "18px",
                border: "1px solid rgba(47,99,173,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                backgroundColor: "rgba(255,255,255,0.96)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                <CheckCircleOutlineIcon sx={{ color: "#2f63ad" }} />
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "#223142" }}
                >
                  Vehicle Information
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: "#6b778c", mb: 2.5 }}>
                Edit the plate, vehicle details, permit fields, dates, and notes.
              </Typography>

              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    required
                    label="License Plate"
                    name="license_plate"
                    value={editForm.license_plate}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={editForm.state}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Property ID"
                    value={editForm.property_id || ""}
                    InputProps={{ readOnly: true }}
                    sx={readonlyFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Make"
                    name="make"
                    value={editForm.make}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Model"
                    name="model"
                    value={editForm.model}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    label="Color"
                    name="color"
                    value={editForm.color}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    label="Year"
                    name="year"
                    type="number"
                    value={editForm.year}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={2}>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="status"
                    value={editForm.status}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="expired">Expired</MenuItem>
                    <MenuItem value="revoked">Revoked</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Permit Type"
                    name="permit_type"
                    value={editForm.permit_type}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  >
                    <MenuItem value="resident">Resident</MenuItem>
                    <MenuItem value="guest">Guest</MenuItem>
                    <MenuItem value="vendor">Vendor</MenuItem>
                    <MenuItem value="contractor">Contractor</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Permit Number"
                    name="permit_number"
                    value={editForm.permit_number}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Sticker Number"
                    name="sticker_number"
                    value={editForm.sticker_number}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="RFID Tag"
                    name="rfid_tag"
                    value={editForm.rfid_tag}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="QR Code"
                    name="qr_code"
                    value={editForm.qr_code}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    name="start_date"
                    type="datetime-local"
                    value={editForm.start_date}
                    onChange={handleEditChange}
                    InputLabelProps={{ shrink: true }}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="End Date"
                    name="end_date"
                    type="datetime-local"
                    value={editForm.end_date}
                    onChange={handleEditChange}
                    InputLabelProps={{ shrink: true }}
                    sx={textFieldSx}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Primary Vehicle"
                    name="is_primary"
                    value={String(editForm.is_primary)}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Blacklisted"
                    name="is_blacklisted"
                    value={String(editForm.is_blacklisted)}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  >
                    <MenuItem value="false">No</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ borderColor: "rgba(47,99,173,0.08)" }} />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Notes"
                    name="notes"
                    value={editForm.notes}
                    onChange={handleEditChange}
                    sx={textFieldSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid rgba(47,99,173,0.08)",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={handleCloseEdit}
            variant="outlined"
            sx={secondaryButtonSx}
            disabled={editSaving}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSaveEdit}
            variant="contained"
            sx={primaryButtonSx}
            disabled={editSaving}
          >
            {editSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 3 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ResidentVehiclesList;